import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import Page from "@insite/mobius/Page";
import { isCrossBrandAuthenticated } from "@tSurya/Common/Utilities/brandPermissionUtils";
import BrandAccessDeniedModal from "@tSurya/Components/BrandAccessDeniedModal";
import React, { createContext, useContext, useEffect } from "react";
import { TYPOGRAPHY } from "@tSurya/theme/design.surya";

import { css } from "styled-components";

export const BrandAccessGuardContext = createContext<boolean>(false);
export const useBrandAccessGuard = () => useContext(BrandAccessGuardContext);


// Surya theme wrapper for MyAccount pages
export const myAccountThemeWrapper = {
    css: css`
        body {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
        
        * {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
        
        h1, h2, h3, h4, h5, h6 {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
            font-style: normal !important;
        }

        h1, h2, h3 {
            font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
        }

        h4, h5, h6 {
            font-weight: ${TYPOGRAPHY.weight.bold} !important;
        }
        
        p, span, div, td, th, a, button, input, textarea, label {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
        
        table th, table td {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
        
        button, .btn {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
        
        a {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
        
        input, select, textarea {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
        
        .data-table th, .data-table td {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
        
        .modal-content *, .modal-body * {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
        
        .modal-title, 
        [data-id="modal-title"], 
        [role="dialog"] h2,
        div[role="dialog"] h2,
        .modal-header h2,
        .modal h2 {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
            font-weight: ${TYPOGRAPHY.weight.bold} !important;
        }
        
        [role="dialog"] * {
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        }
    `,
};

// wrapper component for my account pages
interface MyAccountThemeWrapperProps {
    children: React.ReactNode;
}

export const MyAccountThemeWrapper: React.FC<MyAccountThemeWrapperProps> = ({ children }) => {
    const userName = useAppSelector(state => state.context.session.userName);
    const currentUserIsGuest = useAppSelector(getCurrentUserIsGuest);
    const sessionProperties = useAppSelector(state => state.context.session.properties);
    const isBrandAccessDenied = (!userName || currentUserIsGuest) && isCrossBrandAuthenticated(sessionProperties);

    if (isBrandAccessDenied) {
        return (
            <Page>
                <BrandAccessDeniedModal isOpen={true} closeable={false} />
            </Page>
        );
    }

    return (
        <BrandAccessGuardContext.Provider value={true}>
            <StyledWrapper {...myAccountThemeWrapper}>{children}</StyledWrapper>
        </BrandAccessGuardContext.Provider>
    );
};

export default MyAccountThemeWrapper;